/**
 * 供養引取LP 見積もり・申込フォーム制御
 *
 * 責務:
 *   - 3辺サイズ → 合計サイズ → サイズ区分判定
 *   - 金額計算（仏壇 + 仏具箱数 + 仏壇置台）
 *   - 紹介者コード検証（shop.metafields.kuyo.referral_code と照合）
 *   - 郵便番号 → Apps Script Web App 呼出で引取可否・時間帯区分・カレンダー取得
 *   - 引取希望日ピッカーの可否制御
 *   - 時間帯区分ごとのドロップダウン出し分け
 *   - Shopifyカートへの投入＋cart attributes設定＋/checkoutへ遷移
 */

(function () {
  'use strict';

  // --- 定数 ---

  /** サイズ区分の境界と対応キー（variantマップのキー） */
  const SIZE_BUCKETS = [
    { max: 130, key: '130' },
    { max: 200, key: '200' },
    { max: 250, key: '250' },
    { max: 300, key: '300' },
    { max: 350, key: '350' },
    { max: 400, key: '400' }
  ];
  const MAX_SIZE = 400;

  /**
   * 時間帯区分パターンマップ（現時点では仮ラベル）
   * 実際の時間帯値が判明したらこの定数だけ更新する。
   */
  const TIME_SLOT_PATTERNS = {
    '終日':        { slots: [{ value: '指定なし', label: '指定なし' }], note: '' },
    '4区分':       {
      slots: [
        { value: '8-12',  label: '8時～12時' },
        { value: '12-15', label: '12時～15時' },
        { value: '15-18', label: '15時～18時' },
        { value: '18-21', label: '18時～21時' }
      ],
      note: ''
    },
    '3区分':       {
      slots: [
        { value: '8-12',  label: '8時～12時' },
        { value: '12-18', label: '12時～18時' },
        { value: '18-21', label: '18時～21時' }
      ],
      note: ''
    },
    '3区分（有料）': { slots: [{ value: '指定なし', label: '指定なし' }], note: '' },
    '不可':        { slots: [{ value: '指定不可', label: '指定不可' }] }
  };

  /** 申込日から最短引取日までの日数（土日祝含む） */
  const MIN_LEAD_DAYS = 5;

  // --- DOMユーティリティ ---

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }
  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }
  function yen(n) { return '¥' + Number(n || 0).toLocaleString('ja-JP'); }

  // 紹介者コード照合用：Liquidの | sha256 と同じ小文字hex出力に揃える
  async function sha256Hex(s) {
    const buf = new TextEncoder().encode(s);
    const hashBuf = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hashBuf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function pad2(n) { return String(n).padStart(2, '0'); }
  function toYMD(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }
  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }
  function toYM(d) { return d.getFullYear() + pad2(d.getMonth() + 1); }

  function lastDayOfMonth(ym) {
    var y = Number(ym.substring(0, 4));
    var m = Number(ym.substring(4, 6));
    return new Date(y, m, 0).getDate();
  }

  // カタカナ（長音・全角/半角スペース許可）のみで構成されているか判定。submit時バリデーション用。
  var KATAKANA_REGEX = /^[ァ-ヶー　 ]+$/;

  // 当日連絡先：半角数字のみ（ハイフン・全角不可）。submit時バリデーション用。
  var TEL_REGEX = /^[0-9]+$/;
  
  // カレンダー表示の開始月。最短引取日(5日後)がすでに翌月以降なら、その月を開始月とする。
  function calendarBaseMonth() {
    var today = new Date();
    var minDate = addDays(today, MIN_LEAD_DAYS);
    if (minDate.getFullYear() === today.getFullYear() && minDate.getMonth() === today.getMonth()) {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }
    return new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  }

  function buildDateString(ym, dayIdx) {
    var y = ym.substring(0, 4);
    var m = ym.substring(4, 6);
    var d = String(dayIdx + 1).padStart(2, '0');
    return y + '-' + m + '-' + d;
  }

  // --- 見積もり計算 ---

  function classifyBucket(totalCm) {
    if (totalCm <= 0 || totalCm > MAX_SIZE) return null;
    for (const b of SIZE_BUCKETS) {
      if (totalCm <= b.max) return b;
    }
    return null;
  }

  // --- フォームクラス ---

  function KuyoForm(form) {
    this.form = form;
    this.flow = form.dataset.flow || 'A';
    this.requiresCode = form.dataset.requiresCode === 'true';
    this.variantMap = JSON.parse(form.dataset.butsudanVariants || '{}');
    this.boxVariantId = Number(form.dataset.boxVariantId) || null;
    this.boxBarcode = form.dataset.boxBarcode || '';
    this.standVariantId = Number(form.dataset.standVariantId) || null;
    this.standBarcode = form.dataset.standBarcode || '';
    this.zipCdnBase = form.dataset.zipCdnBase || '';

    this.state = {
      codeVerified: !this.requiresCode,
      zipInfo: null,
      selectedBucket: null, // { max, key }
      totalCm: 0,
      boxCount: 0,
      stand: false,
      submitting: false
    };

    // 親のpage要素から、許可された紹介者コードSHA-256ハッシュ群を取得（plaintextはDOMに出さない）
    // metafield list 由来の30件程度のhashがカンマ区切りで入っている想定
    const pageEl = form.closest('.kuyo-page');
    const hashCsv = pageEl ? (pageEl.dataset.referralCodesHash || '') : '';
    this.correctCodeHashes = new Set(hashCsv.split(',').map(s => s.trim()).filter(Boolean));

    this.bindEvents();
    this.updateTotal();
  }

  KuyoForm.prototype.bindEvents = function () {
    const self = this;
    const form = this.form;

    // サイズ入力変更
    qsa('input[name="butsudan_h"], input[name="butsudan_w"], input[name="butsudan_d"], input[name="box_count"]', form)
      .forEach(el => el.addEventListener('input', () => self.updateTotal()));
    const standEl = qs('input[name="stand"]', form);
    if (standEl) standEl.addEventListener('change', () => self.updateTotal());

    // アクションボタン
    form.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === 'verify-code') return self.verifyCode();
      if (action === 'zip-lookup') return self.lookupZip();
      if (action === 'to-applicant') return self.proceedToApplicant();
      if (action === 'submit') return self.submit();
    });

    // 「お申込者と同じ」チェックで引取先フリガナ入力欄を隠す/表示
    const sameCheckbox = qs('#pickup-kana-same', form);
    if (sameCheckbox) {
      sameCheckbox.addEventListener('change', function () {
        self.togglePickupKanaSame(sameCheckbox.checked);
      });
    }

    // 郵便番号の先行フェッチ: 7桁揃った時点でPhase1を裏で発火
    var zipInput = qs('#kuyo-zip', form);
    var prefetchTimer = null;
    if (zipInput) {
      zipInput.addEventListener('input', function () {
        clearTimeout(prefetchTimer);
        var digits = (zipInput.value || '').replace(/[^0-9]/g, '');
        if (digits.length >= 7 && self.zipCdnBase) {
          prefetchTimer = setTimeout(function () { self.prefetchZip(digits.padStart(7, '0')); }, 300);
        }
      });
    }

    // 時間帯selectは後からoptionが入るためdelegationで受ける
    form.addEventListener('change', function (e) {
      if (e.target && e.target.id === 'kuyo-time') self.syncToApplicantBtn();
    });
  };

  /**
   * CDN JSONを取得しsessionStorageにキャッシュ。
   */
  KuyoForm.prototype.fetchChunk = async function (url, cacheKey) {
    var chunk = browserCache(cacheKey);
    if (chunk) return chunk;
    chunk = await fetchOnce(url);
    browserCacheSet(cacheKey, chunk, BROWSER_CACHE_TTL);
    return chunk;
  };

  /**
   * 7桁入力時にバックグラウンドでpostal + dayを先行取得。
   * postal: 先頭1桁で10分割（kuyo-postal-0.json〜kuyo-postal-9.json）
   * day: 月ごと1ファイル（kuyo-day-YYYYMM.json）不可日のみ記録
   */
  KuyoForm.prototype.prefetchZip = async function (zip) {
    if (!this.zipCdnBase) return;
    var prefix = zip.substring(0, 1);
    try {
      var chunk = await this.fetchChunk(this.zipCdnBase + 'kuyo-postal-' + prefix + '.json', 'postal_chunk_' + prefix);
      var entry = chunk && chunk[zip];
      if (entry && entry.flag === 1) this.prefetchCalendar();
    } catch (e) { /* prefetchなので失敗は無視 */ }
  };

  KuyoForm.prototype.prefetchCalendar = async function () {
    var base = calendarBaseMonth();
    var ym = toYM(base);
    try {
      await this.fetchChunk(this.zipCdnBase + 'kuyo-day-' + ym + '.json', 'day_' + ym);
    } catch (e) { /* ignore */ }
    var next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
    var ymNext = toYM(next);
    try {
      await this.fetchChunk(this.zipCdnBase + 'kuyo-day-' + ymNext + '.json', 'day_' + ymNext);
    } catch (e) { /* 翌月がまだ無い場合は無視 */ }
  };

  KuyoForm.prototype.verifyCode = async function () {
    const input = qs('#kuyo-code', this.form);
    const err = qs('[data-error="referral_code"]', this.form);
    const estimateFs = qs('[data-step="estimate"]', this.form);
    const entered = (input.value || '').trim();
    if (!entered) {
      show(err);
      return;
    }
    const enteredHash = await sha256Hex(entered);
    if (!this.correctCodeHashes.has(enteredHash)) {
      show(err);
      return;
    }
    hide(err);
    this.state.codeVerified = true;
    show(estimateFs);
    estimateFs.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  KuyoForm.prototype.updateTotal = function () {
    const h = Number(qs('input[name="butsudan_h"]', this.form).value) || 0;
    const w = Number(qs('input[name="butsudan_w"]', this.form).value) || 0;
    const d = Number(qs('input[name="butsudan_d"]', this.form).value) || 0;
    const totalCm = h + w + d;
    const bucket = classifyBucket(totalCm);
    this.state.totalCm = totalCm;
    this.state.selectedBucket = bucket;
    this.state.boxCount = Math.min(10, Number(qs('input[name="box_count"]', this.form).value) || 0);
    this.state.stand = !!(qs('input[name="stand"]', this.form) && qs('input[name="stand"]', this.form).checked);

    // 三辺合計表示
    const sizeTotalEl = qs('[data-size-total]', this.form);
    if (sizeTotalEl) sizeTotalEl.textContent = totalCm + 'cm';

    const sizeErr = qs('[data-error="size_over"]', this.form);
    if (totalCm > MAX_SIZE) show(sizeErr); else hide(sizeErr);

    // 仏壇・仏具箱・置台はいずれも税込価格で登録 → そのまま使用（Shopify内部は100倍整数）
    let butsuDanTotal = 0;
    if (bucket && this.variantMap[bucket.key]) {
      butsuDanTotal = Number(this.variantMap[bucket.key].price || 0);
    }
    const boxPrice = Number(this.form.dataset.boxPrice || 0);
    const standPrice = Number(this.form.dataset.standPrice || 0);
    let addOnTotal = boxPrice * this.state.boxCount;
    if (this.state.stand) addOnTotal += standPrice;

    const totalYen = Math.round((butsuDanTotal + addOnTotal) / 100);
    const totalEl = qs('[data-total]', this.form);
    if (totalEl) totalEl.textContent = yen(totalYen);
  };

  // --- ブラウザ側キャッシュ（sessionStorage） ---

  function browserCache(key) {
    try {
      var raw = sessionStorage.getItem('kuyo_' + key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (Date.now() > entry.exp) { sessionStorage.removeItem('kuyo_' + key); return null; }
      return entry.data;
    } catch (e) { return null; }
  }
  function browserCacheSet(key, data, ttlMs) {
    try {
      sessionStorage.setItem('kuyo_' + key, JSON.stringify({ data: data, exp: Date.now() + ttlMs }));
    } catch (e) { /* storage full – ignore */ }
  }
  var BROWSER_CACHE_TTL = 30 * 60 * 1000; // 30分

  // --- 重複リクエスト排除（同じURLのfetchをPromise共有） ---

  var _inflightRequests = {};
  function fetchOnce(url) {
    if (_inflightRequests[url]) return _inflightRequests[url];
    _inflightRequests[url] = fetch(url, { method: 'GET' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .finally(function () { delete _inflightRequests[url]; });
    return _inflightRequests[url];
  }

  // --- 郵便番号検索（CDNチャンク方式） ---

  KuyoForm.prototype.lookupZip = async function () {
    var zipEl = qs('#kuyo-zip', this.form);
    var errEl = qs('[data-error="zip"]', this.form);
    var infoEl = qs('[data-info="zip-address"]', this.form);
    var btn = qs('[data-action="zip-lookup"]', this.form);
    var raw = (zipEl.value || '').replace(/[^0-9]/g, '');
    if (raw.length < 7) { show(errEl); errEl.textContent = '郵便番号は7桁で入力してください'; return; }
    var zip = raw.padStart(7, '0');
    var prefix = zip.substring(0, 1);

    if (!this.zipCdnBase) {
      console.warn('zip_cdn_base not configured - skipping lookup');
      this.state.zipInfo = { pickup_available: true, service_level: '終日', calendar: {} };
      hide(errEl);
      this.renderTimePicker();
      return;
    }

    // 連続押下防止 + ローディング表示 + 前回結果リセット
    btn.disabled = true;
    btn.dataset.origLabel = btn.textContent;
    btn.textContent = '確認中…';
    btn.classList.add('kuyo-btn-loading');
    hide(errEl);
    hide(infoEl);
    hide(qs('[data-field="pickup-date"]', this.form));
    hide(qs('[data-field="pickup-time"]', this.form));

    // --- postalチャンク取得 → 該当zipを検索 ---
    var chunk;
    try {
      chunk = await this.fetchChunk(this.zipCdnBase + 'kuyo-postal-' + prefix + '.json', 'postal_chunk_' + prefix);
    } catch (err) {
      console.error('postal chunk fetch failed', err);
      show(errEl);
      errEl.textContent = '通信エラーが発生しました。時間をおいて再度お試しください。';
      this.resetZipButton(btn);
      return;
    }

    var entry = chunk && chunk[zip];
    if (!entry) {
      show(errEl);
      errEl.textContent = 'この郵便番号は引取対象外エリアです。';
      this.resetZipButton(btn);
      return;
    }

    var pickupAvailable = entry.flag === 1;
    this.state.zipInfo = {
      pickup_available: pickupAvailable,
      address: { prefecture: entry.pref, city: entry.city, street: entry.street },
      service_level: entry.level,
      calendar: {}
    };

    if (!pickupAvailable) {
      show(errEl);
      errEl.textContent = 'この郵便番号は引取対象外エリアです。';
      this.resetZipButton(btn);
      return;
    }

    // 引取可 → 住所表示
    if (infoEl) {
      infoEl.hidden = false;
      infoEl.textContent = entry.pref + ' ' + entry.city + ' ' + entry.street + '（引取可）';
    }

    this.renderTimePicker();

    // --- カレンダー取得 ---
    this.loadCalendar(zip, btn);
  };

  /**
   * 曜日マスタ（day_YYYYMM.json）を取得しカレンダーを構築。
   * 不可日のみ記録形式: { "1600022": [3, 15] } → 3日と15日が不可
   * zipが無い = 全日引取可
   */
  KuyoForm.prototype.loadCalendar = async function (zip, btn) {
    var dateField = qs('[data-field="pickup-date"]', this.form);
    var calendarEl = qs('[data-calendar]', this.form);
    show(dateField);

    var base = calendarBaseMonth();
    var ym = toYM(base);
    var next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
    var ymNext = toYM(next);
    var ymList = [ym, ymNext];
    var calendar = {};

    if (calendarEl) calendarEl.innerHTML = '<p class="kuyo-cal-loading">カレンダーを読み込み中…</p>';

    for (var i = 0; i < ymList.length; i++) {
      var currentYm = ymList[i];
      var dayData;
      try {
        dayData = await this.fetchChunk(
          this.zipCdnBase + 'kuyo-day-' + currentYm + '.json',
          'day_' + currentYm
        );
      } catch (e) {
        calendar[currentYm] = { available: false, reason: 'sheet_not_found' };
        continue;
      }

      // 不可日リスト（無い = 全日可）
      var unavailDays = dayData[zip] || [];
      var lastDay = lastDayOfMonth(currentYm);
      var days = [];
      for (var d = 0; d < lastDay; d++) {
        var dayNum = d + 1;
        days.push({
          date: buildDateString(currentYm, d),
          available: unavailDays.indexOf(dayNum) === -1
        });
      }
      calendar[currentYm] = { available: true, days: days };
    }

    this.state.zipInfo.calendar = calendar;
    this.renderCalendar();
    this.resetZipButton(btn);
  };

  KuyoForm.prototype.resetZipButton = function (btn) {
    btn.disabled = false;
    btn.textContent = btn.dataset.origLabel || '対応エリアか確認する';
    btn.classList.remove('kuyo-btn-loading');
  };

  KuyoForm.prototype.renderTimePicker = function () {
    const timeField = qs('[data-field="pickup-time"]', this.form);
    const timeEl = qs('#kuyo-time', this.form);
    show(timeField);

    const level = this.state.zipInfo && this.state.zipInfo.service_level || '終日';
    const pattern = TIME_SLOT_PATTERNS[level] || TIME_SLOT_PATTERNS['終日'];
    timeEl.innerHTML = '';
    pattern.slots.forEach(s => {
      const opt = document.createElement('option');
      opt.value = s.value;
      opt.textContent = s.label;
      timeEl.appendChild(opt);
    });
    this.syncToApplicantBtn();
  };

  // 引取希望日・時間帯の両方が確定したら「次へ」ボタンを赤(primary)に戻す
  KuyoForm.prototype.syncToApplicantBtn = function () {
    var btn = qs('[data-action="to-applicant"]', this.form);
    if (!btn) return;
    var dateEl = qs('#kuyo-date', this.form);
    var timeEl = qs('#kuyo-time', this.form);
    var dateOk = !!(dateEl && dateEl.value);
    var timeOk = !!(timeEl && timeEl.value);
    if (dateOk && timeOk) btn.classList.remove('is-disabled');
    else btn.classList.add('is-disabled');
  };

  /**
   * 引取可能日だけ選択できるカレンダーグリッドを描画する。
   * 不可日・最短引取日より前の日はクリック不可。
   */
  KuyoForm.prototype.renderCalendar = function () {
    const container = qs('[data-calendar]', this.form);
    const hiddenInput = qs('#kuyo-date', this.form);
    const info = this.state.zipInfo;
    if (!container || !info) return;

    const minDate = toYMD(addDays(new Date(), MIN_LEAD_DAYS));

    // APIレスポンスから全available日を集める
    var availableDates = {};
    for (var ym in info.calendar) {
      var c = info.calendar[ym];
      if (!c.available || !c.days) continue;
      c.days.forEach(function (d) { availableDates[d.date] = d.available; });
    }

    // 表示対象の2ヶ月を決定。最短引取日(5日後)が翌月以降なら開始を1ヶ月後ろにシフト。
    var base = calendarBaseMonth();
    var months = [
      { y: base.getFullYear(), m: base.getMonth() },
      { y: base.getFullYear(), m: base.getMonth() + 1 }
    ];
    // 月のオーバーフロー補正
    months.forEach(function (mo) {
      if (mo.m > 11) { mo.y++; mo.m -= 12; }
    });

    container.innerHTML = '';
    var self = this;

    months.forEach(function (mo) {
      var year = mo.y;
      var month = mo.m;
      var firstDay = new Date(year, month, 1).getDay(); // 0=日
      var daysInMonth = new Date(year, month + 1, 0).getDate();

      var section = document.createElement('div');
      section.className = 'kuyo-cal-month';

      var header = document.createElement('div');
      header.className = 'kuyo-cal-header';
      header.textContent = year + '年' + (month + 1) + '月';
      section.appendChild(header);

      var table = document.createElement('table');
      table.className = 'kuyo-cal-table';
      var thead = document.createElement('thead');
      var headRow = document.createElement('tr');
      ['日','月','火','水','木','金','土'].forEach(function (d) {
        var th = document.createElement('th');
        th.textContent = d;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      var tbody = document.createElement('tbody');
      var row = document.createElement('tr');

      // 先頭の空セル
      for (var i = 0; i < firstDay; i++) {
        row.appendChild(document.createElement('td'));
      }

      for (var day = 1; day <= daysInMonth; day++) {
        var dateStr = year + '-' + pad2(month + 1) + '-' + pad2(day);
        var td = document.createElement('td');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = day;
        btn.dataset.date = dateStr;

        var isAvailable = availableDates[dateStr] === true;
        var isPast = dateStr < minDate;

        if (!isAvailable || isPast) {
          btn.disabled = true;
          btn.className = 'kuyo-cal-day kuyo-cal-disabled';
        } else {
          btn.className = 'kuyo-cal-day kuyo-cal-available';
          btn.addEventListener('click', (function (ds) {
            return function () {
              // 選択状態の更新
              qsa('.kuyo-cal-selected', container).forEach(function (el) {
                el.classList.remove('kuyo-cal-selected');
              });
              this.classList.add('kuyo-cal-selected');
              hiddenInput.value = ds;
              self.syncToApplicantBtn();
            };
          })(dateStr));
        }

        td.appendChild(btn);
        row.appendChild(td);

        if ((firstDay + day) % 7 === 0) {
          tbody.appendChild(row);
          row = document.createElement('tr');
        }
      }
      // 末尾の空セル
      var remaining = (firstDay + daysInMonth) % 7;
      if (remaining > 0) {
        for (var j = remaining; j < 7; j++) {
          row.appendChild(document.createElement('td'));
        }
        tbody.appendChild(row);
      }

      table.appendChild(tbody);
      section.appendChild(table);
      container.appendChild(section);
    });
  };

  KuyoForm.prototype.proceedToApplicant = function () {
    if (!this.state.selectedBucket) {
      alert('お仏壇のサイズをご入力ください。');
      return;
    }
    if (this.state.totalCm > MAX_SIZE) {
      alert('三辺合計が400cmを超えるお仏壇は引取対象外です。');
      return;
    }
    if (!this.state.zipInfo) {
      alert('郵便番号を入力し「対応エリアか確認する」ボタンを押してください。');
      return;
    }
    const dateEl = qs('#kuyo-date', this.form);
    if (!dateEl.value) {
      alert('引取希望日をお選びください。');
      return;
    }
    const applicantFs = qs('[data-step="applicant"]', this.form);
    show(applicantFs);
    applicantFs.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  KuyoForm.prototype.submit = async function () {
    // 二重送信防止：処理中フラグ・ボタン disabled でクリックを排他
    if (this.state.submitting) return;
    if (!this.validateApplicantForm()) return;

    const submitBtn = qs('[data-action="submit"]', this.form);
    const loading = qs('[data-loading]', this.form);
    this.state.submitting = true;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.dataset.origLabel = submitBtn.textContent;
      submitBtn.textContent = 'しばらくお待ちください';
      submitBtn.classList.add('kuyo-btn-loading');
    }
    show(loading);

    const releaseSubmitGuard = () => {
      this.state.submitting = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        if (submitBtn.dataset.origLabel) submitBtn.textContent = submitBtn.dataset.origLabel;
        submitBtn.classList.remove('kuyo-btn-loading');
      }
      hide(loading);
    };

    const bucket = this.state.selectedBucket;
    const variant = this.variantMap[bucket.key];
    if (!variant) {
      alert('商品情報の読込に失敗しました。ページを再読み込みしてください。');
      releaseSubmitGuard();
      return;
    }

    const hEl = qs('input[name="butsudan_h"]', this.form);
    const wEl = qs('input[name="butsudan_w"]', this.form);
    const dEl = qs('input[name="butsudan_d"]', this.form);
    const sizeLabel = `高さ${hEl.value}cm × 幅${wEl.value}cm × 奥行${dEl.value}cm = ${this.state.totalCm}cm`;

    // line items
    const items = [{
      id: variant.id,
      quantity: 1,
      properties: {
        '実測サイズ': sizeLabel
      }
    }];
    if (this.state.boxCount > 0 && this.boxVariantId) {
      items.push({ id: this.boxVariantId, quantity: this.state.boxCount });
    }
    if (this.state.stand && this.standVariantId) {
      items.push({ id: this.standVariantId, quantity: 1 });
    }

    // cart attributes
    const attrs = this.collectCartAttributes(variant);

    try {
      // 供養は単品申込前提のため、既存カートに他商品があれば「数量0」で個別ゼロ化する。
      // /cart/clear.js は Shopify前段のCloudflareが Bot Fight Mode で
      // Challenge を出しやすいAPI（カート全消去）。代わりに通常のqty変更APIで
      // 個別ゼロ化することでCloudflareから自然な動線に見せる。
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        if (cart && Array.isArray(cart.items) && cart.items.length > 0) {
          const clearForm = new FormData();
          cart.items.forEach(function (it) {
            clearForm.append('updates[' + it.variant_id + ']', '0');
          });
          await fetch('/cart/update.js', { method: 'POST', body: clearForm, credentials: 'same-origin' });
          await sleep(250);
        }
      }

      // 商品追加
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
        credentials: 'same-origin'
      });
      if (!addRes.ok) throw new Error('cart add failed: ' + addRes.status);
      await sleep(250);

      // cart attributes更新
      const attrForm = new FormData();
      Object.keys(attrs).forEach(k => attrForm.append(`attributes[${k}]`, attrs[k]));
      await fetch('/cart/update.js', { method: 'POST', body: attrForm, credentials: 'same-origin' });
      await sleep(250);

      // checkoutへ遷移（遷移直前までフラグは解除しない＝戻る操作での再送信も防止）
      window.location.href = '/checkout';
    } catch (err) {
      console.error(err);
      alert('お申込み処理に失敗しました。時間をおいて再度お試しください。');
      releaseSubmitGuard();
    }
  };

  KuyoForm.prototype.togglePickupKanaSame = function (same) {
    const pickupKanaField = qs('[data-field="pickup-kana"]', this.form);
    const pickupLast = qs('#pickup-lastname-kana', this.form);
    const pickupFirst = qs('#pickup-firstname-kana', this.form);
    if (!pickupKanaField || !pickupLast || !pickupFirst) return;
    if (same) {
      pickupLast.value = qs('#app-lastname-kana', this.form).value || '';
      pickupFirst.value = qs('#app-firstname-kana', this.form).value || '';
      pickupLast.required = false;
      pickupFirst.required = false;
      hide(pickupKanaField);
    } else {
      pickupLast.required = true;
      pickupFirst.required = true;
      show(pickupKanaField);
    }
  };

  KuyoForm.prototype.validateApplicantForm = function () {
    const required = qsa('[data-step="applicant"] [required]', this.form);
    for (const el of required) {
      if (!el.value || (el.checkValidity && !el.checkValidity())) {
        el.focus();
        alert('未入力・不備のある項目があります。');
        return false;
      }
    }

    // カナ4欄は全角カタカナのみ許可（「お申込者と同じ」チェック時は引取先カナをスキップ）
    const sameCheckbox = qs('#pickup-kana-same', this.form);
    const pickupKanaSame = !!(sameCheckbox && sameCheckbox.checked);
    const kanaTargets = [
      '#app-lastname-kana',
      '#app-firstname-kana'
    ];
    if (!pickupKanaSame) {
      kanaTargets.push('#pickup-lastname-kana', '#pickup-firstname-kana');
    }
    for (const sel of kanaTargets) {
      const el = qs(sel, this.form);
      if (el && el.value && !KATAKANA_REGEX.test(el.value)) {
        el.focus();
        alert('カナ欄は全角カタカナでご入力ください。\n（例：ハセガワ / タロウ）');
        return false;
      }
    }

    // 当日連絡先は半角数字のみ（ハイフン・全角は不可）
    const telEl = qs('#app-tel-day', this.form);
    if (telEl && telEl.value && !TEL_REGEX.test(telEl.value)) {
      telEl.focus();
      alert('当日連絡先は半角数字でご入力ください。\n（ハイフンや全角文字は使えません。例：09012345678）');
      return false;
    }
    return true;
  };

  KuyoForm.prototype.collectCartAttributes = function (variant) {
    const f = this.form;
    const v = name => {
      const el = qs(`[name="${name}"]`, f);
      return el ? el.value : '';
    };
    const applicantLastnameKana = v('applicant_lastname_kana');
    const applicantFirstnameKana = v('applicant_firstname_kana');
    const same = !!(qs('#pickup-kana-same', f) && qs('#pickup-kana-same', f).checked);
    const pickupLastnameKana = same ? applicantLastnameKana : v('pickup_lastname_kana');
    const pickupFirstnameKana = same ? applicantFirstnameKana : v('pickup_firstname_kana');

    // 氏名（漢字）・住所・メール・電話は Shopify checkout の billing/shipping を真とする。
    // LP では checkout では取れない項目（フリガナ・当日連絡先）と、引取可否判定用の郵便番号のみを cart attribute へ渡す。
    const attrs = {
      '_flow': this.flow,
      '_applicant_lastname_kana': applicantLastnameKana,
      '_applicant_firstname_kana': applicantFirstnameKana,
      '_applicant_kana': `${applicantLastnameKana} ${applicantFirstnameKana}`.trim(),
      '_applicant_phone_day': v('applicant_phone_day'),
      '_pickup_lastname_kana': pickupLastnameKana,
      '_pickup_firstname_kana': pickupFirstnameKana,
      '_pickup_kana': `${pickupLastnameKana} ${pickupFirstnameKana}`.trim(),
      '_pickup_kana_same': same ? '1' : '0',
      '_pickup_zip_lp': v('pickup_zip'),
      // `_` プレフィックスを外して日本語キーにすることで、Shopify の注文画面・
      // 注文メールに顧客向けの項目として可視化される。
      '引き取り希望日': v('pickup_date'),
      '引き取り希望時間帯': v('pickup_time_slot'),
      '_order_jan': variant.barcode || ''
    };
    if (this.state.boxCount > 0) attrs['_order_jan_box'] = this.boxBarcode;
    if (this.state.stand) attrs['_order_jan_stand'] = this.standBarcode;
    if (this.flow !== 'A') {
      const codeEl = qs('#kuyo-code', f);
      if (codeEl) attrs['_referral_code'] = codeEl.value;
    }
    return attrs;
  };

  // --- 起動 ---

  document.addEventListener('DOMContentLoaded', function () {
    qsa('[data-kuyo-form]').forEach(f => new KuyoForm(f));
  });
})();
